// useSectorFill — the compass ring fill's state (Compass Sector Fill spec, 18 Oct 2026 §4–5).
// Targets come from the shared computeComplete (sectorProgress.ts), so the
// compass fill always matches Home and the sector overview. Each change eases
// over 600 ms (ease-in-out). When a project finishes, the sector's fill holds
// its old value while the completion flare runs (~1.2 s, outer → inner), the
// toast fires, and only then does the pool change re-animate the fill.
import { useEffect, useMemo, useRef, useState } from 'react';
import { SECTORS, SECTOR_LABELS, type Sector } from '../domain/olmps';
import type { OracleProject } from './useOracleProjects';
import { computeComplete, projectLeaves, type KyrsLeafTask } from './sectorProgress';

const FILL_MS = 600;
export const FLARE_MS = 1200;

type Targets = Record<string, number | null>;
const easeInOut = (p: number) => (p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2);

/** Fill key for a sector spoke, or for a SOLO project spoke. */
export const fillKey = (sector: Sector, projectId?: string | null) => (projectId ? `p:${projectId}` : sector);

export interface SectorFill {
  /** Animated 0..1 fill for a key (null = "—", draw nothing). */
  valueOf: (key: string) => number | null;
  /** The settled target (100% glow reads this). */
  targetOf: (key: string) => number | null;
  /** Running completion flare: its sector and progress 0..1. */
  flare: { sector: Sector; p: number } | null;
}

export function useSectorFill(
  projects: Record<Sector, OracleProject[]>,
  isDemo: boolean,
  seasonDays: number,
  onFinished: (msg: string) => void,
  tasks: KyrsLeafTask[] = [],
): SectorFill {
  const targets = useMemo<Targets>(() => {
    const src: Partial<Record<Sector, OracleProject[]>> = isDemo ? {} : projects;
    const out: Targets = {};
    for (const s of SECTORS) {
      out[s] = computeComplete(s, src, { now: new Date(), seasonDays, tasks }).complete;
      for (const p of src[s] ?? []) {
        const leaves = projectLeaves(p);
        out[fillKey(s, p.id)] = leaves.length ? leaves.filter((l) => l.done).length / leaves.length : null;
      }
    }
    return out;
  }, [projects, isDemo, seasonDays, tasks]);

  // ---- §5 completion flare: a project that was in the pool is now complete.
  const prevPool = useRef<Map<string, { sector: Sector; name: string }> | null>(null);
  const prevTargets = useRef<Targets>(targets);
  const [held, setHeld] = useState<Targets | null>(null);
  const [flare, setFlare] = useState<{ sector: Sector; p: number } | null>(null);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;
  useEffect(() => {
    const src: Partial<Record<Sector, OracleProject[]>> = isDemo ? {} : projects;
    const pool = new Map<string, { sector: Sector; name: string }>();
    const finishedNow: { sector: Sector; name: string }[] = [];
    for (const s of SECTORS) {
      const c = computeComplete(s, src, { seasonDays });
      for (const p of c.active) pool.set(p.id, { sector: s, name: p.name });
      for (const p of c.finished) if (prevPool.current?.has(p.id)) finishedNow.push({ sector: s, name: p.name });
    }
    const before = prevTargets.current;
    prevPool.current = pool;
    prevTargets.current = targets;
    if (!finishedNow.length) return;
    const first = finishedNow[0];
    setHeld({ ...targets, [first.sector]: before[first.sector] ?? null });
    for (const f of finishedNow) onFinishedRef.current(`${f.name} finished · ${SECTOR_LABELS[f.sector]}`);
    const t0 = Date.now();
    let raf = 0;
    const tick = () => {
      const p = Math.min(1, (Date.now() - t0) / FLARE_MS);
      setFlare({ sector: first.sector, p });
      if (p < 1) raf = requestAnimationFrame(tick);
      else {
        setFlare(null);
        setHeld(null); // apply the pool change → the fill re-animates
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [targets, projects, isDemo, seasonDays]);

  // ---- 600 ms ease-in-out toward the goal values.
  const goal = held ?? targets;
  const [shown, setShown] = useState<Record<string, number>>(() => Object.fromEntries(Object.entries(goal).map(([k, v]) => [k, v ?? 0])));
  const shownRef = useRef(shown);
  shownRef.current = shown;
  useEffect(() => {
    const from = shownRef.current;
    const to = Object.fromEntries(Object.entries(goal).map(([k, v]) => [k, v ?? 0]));
    if (Object.keys(to).every((k) => Math.abs((from[k] ?? 0) - to[k]) < 1e-4)) return;
    const t0 = Date.now();
    let raf = 0;
    const tick = () => {
      const e = easeInOut(Math.min(1, (Date.now() - t0) / FILL_MS));
      setShown(Object.fromEntries(Object.keys(to).map((k) => [k, (from[k] ?? 0) + (to[k] - (from[k] ?? 0)) * e])));
      if (e < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [goal]);

  return {
    valueOf: (k) => (goal[k] == null && (shown[k] ?? 0) < 1e-3 ? null : shown[k] ?? 0),
    targetOf: (k) => goal[k] ?? null,
    flare,
  };
}
